// ── STATE TAX ─────────────────────────────────────────────────────────────────
// Per-state credentials + resale registration. Owner-only. Lives at
// Admin → State Tax. Card-per-state layout with edit modal, password-reveal
// toggle, "Open Portal" link, and a hint to the matching resale-cert form.
//
// Sensitive data: passwords and account IDs. RLS enforces owner-only at DB.

import React, { useState, useMemo } from 'react';
import { useAppContext } from './AppContext';
import { STATE_TAX_DOCUMENT_KINDS, stateTaxDocumentKindLabel, stateTaxDocumentExpiryState } from '../utils';

export var US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['FL','Florida'],['GA','Georgia'],
  ['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],
  ['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
  ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],
  ['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],
  ['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
  ['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
  ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],
  ['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
];

var STATUS_STYLE = {
  active:        { dot: '#1A5C38', label: 'Active' },
  pending:       { dot: '#F5C800', label: 'Pending' },
  not_required:  { dot: '#888880', label: 'Not required' },
  closed:        { dot: '#C8322A', label: 'Closed' },
};

var inp = {
  width: '100%', padding: '8px 12px', fontSize: 13, fontFamily: 'inherit',
  border: '0.5px solid #E0E0DC', borderRadius: 6, background: '#fff',
  outline: 'none', boxSizing: 'border-box',
};
var lbl = { fontSize: 10, fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888', marginBottom: 4, display: 'block' };
var btnGhost = { height: 32, padding: '0 14px', borderRadius: 18, border: '0.5px solid #E0E0DC', background: '#fff', color: '#111', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' };
var btnPrimary = { height: 32, padding: '0 16px', borderRadius: 18, border: 'none', background: '#111', color: '#fff', fontSize: 12, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' };
var btnDanger = { height: 28, padding: '0 12px', borderRadius: 14, border: '0.5px solid #C8322A', background: '#fff', color: '#C8322A', fontSize: 11, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer' };
var linkBtn = { height: 26, padding: '0 12px', borderRadius: 14, border: '0.5px solid #E0E0DC', background: '#fff', color: '#111', fontSize: 11, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' };

export default function StateTaxSection() {
  var ctx = useAppContext();
  var stateTaxRecords = ctx.stateTaxRecords || [];
  var saveStateTax = ctx.saveStateTax;
  var deleteStateTax = ctx.deleteStateTax;
  var stateTaxDocuments = ctx.stateTaxDocuments || [];
  var isOwner = ctx.isOwner;

  var ms = useState(null);          var modalRec = ms[0];     var setModalRec = ms[1];
  var rs = useState({});            var revealed = rs[0];     var setRevealed = rs[1];
  var cfs = useState(null);         var copiedField = cfs[0]; var setCopiedField = cfs[1];

  var sorted = useMemo(function() {
    return stateTaxRecords.slice().sort(function(a, b) {
      var c = String(a.stateCode || '').localeCompare(String(b.stateCode || ''));
      if (c !== 0) return c;
      // The state itself first, its cities after.
      return String(a.jurisdiction || '').localeCompare(String(b.jurisdiction || ''));
    });
  }, [stateTaxRecords]);

  if (!isOwner) {
    return (
      <div style={{ padding: '40px 28px', maxWidth: 600, fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ padding: '40px 24px', textAlign: 'center', color: '#888', fontSize: 13, fontStyle: 'italic', background: '#FAFAF8', border: '0.5px solid #E0E0DC', borderRadius: 10 }}>
          State Tax records are owner-only.
        </div>
      </div>
    );
  }

  function openNew() {
    setModalRec({
      id: null, stateCode: '', jurisdiction: '', stateName: '', status: 'active',
      registrationId: '', businessPartnerId: '', accountEmail: '',
      username: '', password: '', pin: '', portalUrl: '', certFormLabel: '',
      supportPhone: '', notes: '',
      taxBasis: 'payment', filingFrequency: 'monthly', dueRule: '', registeredFrom: '',
    });
  }

  function openEdit(rec) {
    setModalRec(Object.assign({}, rec));
  }

  function toggleReveal(id) {
    setRevealed(function(prev) { var n = Object.assign({}, prev); n[id] = !n[id]; return n; });
  }

  // Copy with fallback. The modern Clipboard API silently fails when the
  // document doesn't have focus (DevTools open, focus on another window),
  // when the page isn't a secure context, or when the browser blocks the
  // call. Falls back to the legacy execCommand path so the button works
  // in those cases too. Tracks "just copied" state so the button shows
  // "Copied!" briefly as feedback.
  function copyText(txt, fieldId) {
    if (!txt) return;
    var done = function() {
      if (fieldId != null) {
        setCopiedField(fieldId);
        setTimeout(function() { setCopiedField(null); }, 1200);
      }
    };
    var fail = function(reason) {
      console.warn('copyText failed:', reason);
      // Last-resort fallback: hidden textarea + execCommand. Works in
      // every browser back to IE9-ish and isn't subject to focus rules.
      try {
        var ta = document.createElement('textarea');
        ta.value = txt;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.focus(); ta.select();
        var ok = document.execCommand('copy');
        document.body.removeChild(ta);
        if (ok) done();
        else alert('Could not copy. Select the text and copy manually (Cmd+C / Ctrl+C).');
      } catch (e) {
        alert('Could not copy: ' + e.message);
      }
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(txt).then(done).catch(fail);
    } else {
      fail('clipboard API unavailable');
    }
  }

  function removeRec(rec) {
    if (!window.confirm('Delete the ' + (rec.jurisdiction ? rec.jurisdiction + ', ' : '') + rec.stateName + ' tax record? This cannot be undone.')) return;
    deleteStateTax(rec.id);
  }

  return (
    <div style={{ padding: '20px 24px 40px', maxWidth: 1100, fontFamily: "'DM Sans', sans-serif" }}>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#111' }}>State Tax</div>
          <div style={{ fontSize: 12, color: '#888' }}>
            Per-state portal credentials, resale registration, and notes. Owner-only.
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button style={btnPrimary} onClick={openNew}>+ Add State</button>
      </div>

      {sorted.length === 0 ? (
        <div style={{ padding: '50px 24px', textAlign: 'center', color: '#888', fontSize: 13, fontStyle: 'italic', background: '#FAFAF8', border: '0.5px solid #E0E0DC', borderRadius: 10 }}>
          No state records yet.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: 14 }}>
          {sorted.map(function(rec) {
            var s = STATUS_STYLE[rec.status] || STATUS_STYLE.active;
            var pwShown = !!revealed[rec.id];
            return (
              <div key={rec.id} style={{ background: '#fff', border: '0.5px solid #E0E0DC', borderRadius: 10, padding: 18 }}>

                {/* Card header */}
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
                  <div style={{ fontSize: 18, fontWeight: 600, color: '#111' }}>{rec.stateCode}{rec.jurisdiction ? <span style={{ fontWeight: 400, color: '#666' }}> · {rec.jurisdiction}</span> : null}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>{rec.jurisdiction ? 'home-rule city · ' + rec.stateName : rec.stateName}</div>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#666' }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.dot }} />
                    {s.label}
                  </span>
                </div>

                {/* Key fields */}
                <FieldRow label="Resale / Reg ID" value={rec.registrationId} onCopy={copyText} fieldId={rec.id+':reg'} copiedField={copiedField} />
                {rec.businessPartnerId && <FieldRow label="Business Partner" value={rec.businessPartnerId} onCopy={copyText} fieldId={rec.id+':bp'} copiedField={copiedField} />}
                {rec.accountEmail && <FieldRow label="Account email" value={rec.accountEmail} onCopy={copyText} fieldId={rec.id+':email'} copiedField={copiedField} />}
                {rec.username && <FieldRow label="Username" value={rec.username} onCopy={copyText} fieldId={rec.id+':user'} copiedField={copiedField} />}
                {rec.password && (
                  <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr auto', gap: 8, alignItems: 'center', padding: '4px 0' }}>
                    <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#888' }}>Password</div>
                    <div style={{ fontSize: 12, fontFamily: pwShown ? 'inherit' : 'monospace', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {pwShown ? rec.password : '••••••••'}
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button style={Object.assign({}, linkBtn, { height: 22, padding: '0 8px', fontSize: 10 })} onClick={function() { toggleReveal(rec.id); }}>
                        {pwShown ? 'Hide' : 'Show'}
                      </button>
                      <button style={Object.assign({}, linkBtn, { height: 22, padding: '0 8px', fontSize: 10 })} onClick={function() { copyText(rec.password, rec.id+':pw'); }}>
                        {copiedField === rec.id+':pw' ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>
                )}
                {rec.pin && (
                  <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr auto', gap: 8, alignItems: 'center', padding: '4px 0' }} data-testid={'state-tax-pin-' + rec.id}>
                    <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#888' }}>PIN</div>
                    <div style={{ fontSize: 12, fontFamily: pwShown ? 'inherit' : 'monospace', color: '#111', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {pwShown ? rec.pin : '••••'}
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button style={Object.assign({}, linkBtn, { height: 22, padding: '0 8px', fontSize: 10 })} onClick={function() { toggleReveal(rec.id); }}>
                        {pwShown ? 'Hide' : 'Show'}
                      </button>
                      <button style={Object.assign({}, linkBtn, { height: 22, padding: '0 8px', fontSize: 10 })} onClick={function() { copyText(rec.pin, rec.id+':pin'); }}>
                        {copiedField === rec.id+':pin' ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                  </div>
                )}
                {rec.certFormLabel && <FieldRow label="Cert form" value={rec.certFormLabel} />}
                {rec.supportPhone && <FieldRow label="Support" value={rec.supportPhone} />}

                <FilingRules rec={rec} onSave={saveStateTax} />
                <StateTaxDocuments rec={rec} docs={stateTaxDocuments.filter(function(d) { return d.stateTaxRecordId === rec.id; })}
                                   onAdd={ctx.addStateTaxDocument} onRemove={ctx.deleteStateTaxDocument} getUrl={ctx.stateTaxDocumentUrl} />

                {rec.notes && (
                  <div style={{ marginTop: 10, padding: '10px 12px', background: '#FAFAF8', border: '0.5px solid #E0E0DC', borderRadius: 6, fontSize: 11, color: '#555', lineHeight: 1.5 }}>
                    {rec.notes}
                  </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: 6, marginTop: 14, flexWrap: 'wrap' }}>
                  {rec.portalUrl && (
                    <a href={rec.portalUrl} target="_blank" rel="noopener noreferrer" style={linkBtn}>
                      Open Portal ↗
                    </a>
                  )}
                  <button style={linkBtn} onClick={function() { openEdit(rec); }}>Edit</button>
                  <div style={{ flex: 1 }} />
                  <button style={btnDanger} onClick={function() { removeRec(rec); }}>Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modalRec && (
        <StateTaxModal
          rec={modalRec}
          onClose={function() { setModalRec(null); }}
          onSave={function(updated) { saveStateTax(updated); setModalRec(null); }}
        />
      )}
    </div>
  );
}

// ── Filing rules (2026-10-16, design handoff v2 screen 04). Four fields on
// the state record, edited in place on the card: period basis, frequency,
// return due, registered from. Pills save on click; the date saves on
// change. Grid 130px 1fr gap 10px 12px; 12px INK_3 labels; 26px pills —
// selected 1.5px solid #111111 + #111111 text, else 0.5px solid #D8D7D2 +
// #888880 text; date input width 160; helper copy 11px #555552 lh 1.6.
export var FILING_RULE_OPTIONS = {
  taxBasis: [['payment', 'Client payment'], ['delivery', 'Delivery into state']],
  filingFrequency: [['monthly', 'Monthly'], ['quarterly', 'Quarterly'], ['annual', 'Annual']],
  dueRule: [['20_next_month', '20th of next month'], ['last_day_next_month', 'Last day of next month']],
};
function rulePill(on) {
  return {
    height: 26, padding: '0 12px', borderRadius: 13, fontFamily: 'inherit', fontSize: 12, cursor: 'pointer',
    border: on ? '1.5px solid #111111' : '0.5px solid #D8D7D2', background: '#fff', color: on ? '#111111' : '#888880',
  };
}
export function FilingRules(p) {
  var rec = p.rec || {};
  function setField(k, v) {
    if (!p.onSave) return;
    var next = Object.assign({}, rec); next[k] = v;
    p.onSave(next);
  }
  function group(k) {
    var cur = k === 'taxBasis' ? (rec.taxBasis === 'delivery' ? 'delivery' : 'payment')
      : k === 'filingFrequency' ? (rec.filingFrequency || 'monthly')
      : (rec.dueRule || '');
    return (
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} data-testid={'filing-rule-' + k}>
        {FILING_RULE_OPTIONS[k].map(function(opt) {
          var on = cur === opt[0];
          return <button key={opt[0]} type="button" onClick={function() { if (!on) setField(k, opt[0]); }} style={rulePill(on)} aria-pressed={on}>{opt[1]}</button>;
        })}
      </div>
    );
  }
  var lab = { fontSize: 12, color: '#888880', alignSelf: 'center' };
  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '0.5px solid #EFEFEC' }} data-testid={'filing-rules-' + rec.id}>
      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#888', marginBottom: 8 }}>Filing rules</div>
      <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '10px 12px', alignItems: 'center' }}>
        <div style={lab}>Period basis</div>{group('taxBasis')}
        <div style={lab}>Frequency</div>{group('filingFrequency')}
        <div style={lab}>Return due</div>{group('dueRule')}
        <div style={lab}>Registered from</div>
        <div>
          <input type="date" value={rec.registeredFrom || ''} onChange={function(e) { setField('registeredFrom', e.target.value); }}
                 style={{ width: 160, padding: '7px 10px', fontSize: 13, fontFamily: 'inherit', border: '0.5px solid #D8D7D2', borderRadius: 6, background: '#fff', outline: 'none', boxSizing: 'border-box', color: '#111' }}
                 data-testid={'filing-rule-registeredFrom'} />
        </div>
      </div>
      <div style={{ fontSize: 11, color: '#555552', lineHeight: 1.6, marginTop: 12, paddingTop: 10, borderTop: '0.5px solid #EFEFEC' }}>
        On <span style={{ color: '#111111' }}>delivery basis</span> an item lands in the period of its <span style={{ color: '#111111' }}>Delivered to state</span> date (default: the project&rsquo;s install date; editable per item). Items delivered before <span style={{ color: '#111111' }}>Registered from</span> are flagged on the Sales Tax page, not silently dropped. No special mode is needed for the first return &mdash; it is simply a large normal row.
      </div>
    </div>
  );
}

// ── Documents (2026-10-16): the resale certificate / licenses behind the
// registration, one list per card. Pick a kind, optional expiry, choose
// the file — it uploads on choose. Hairlines, no fills; expiry prints in
// red when passed, amber within 60 days.
export function StateTaxDocuments(p) {
  var rec = p.rec || {};
  var docs = p.docs || [];
  var ks = useState('resale_certificate'); var kind = ks[0]; var setKind = ks[1];
  var es = useState('');                   var expires = es[0]; var setExpires = es[1];
  var bs = useState(false);                var busy = bs[0]; var setBusy = bs[1];
  var errs = useState('');                 var err = errs[0]; var setErr = errs[1];
  var inputRef = React.useRef(null);
  var sel = { height: 26, padding: '0 8px', fontSize: 12, fontFamily: 'inherit', border: '0.5px solid #D8D7D2', borderRadius: 6, background: '#fff', color: '#111' };
  async function onPick(e) {
    var file = e.target.files && e.target.files[0]; e.target.value = '';
    if (!file || !p.onAdd) return;
    setBusy(true); setErr('');
    var r;
    try { r = await p.onAdd(rec, file, { kind: kind, expiresOn: expires || null }); }
    catch (ex) { r = { ok: false, error: 'Upload failed: ' + (ex && ex.message ? ex.message : 'unknown error') }; }
    setBusy(false);
    if (!r || !r.ok) setErr((r && r.error) || 'Upload failed.'); else setExpires('');
  }
  async function open(doc) {
    if (!p.getUrl) return;
    var url = await p.getUrl(doc.storagePath);
    if (url) window.open(url, '_blank', 'noopener');
  }
  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '0.5px solid #EFEFEC' }} data-testid={'state-tax-docs-' + rec.id}>
      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#888', marginBottom: 8 }}>Documents</div>
      {docs.length === 0 && <div style={{ fontSize: 11, color: '#888880', marginBottom: 8 }}>No resale certificate or license on file.</div>}
      {docs.map(function(d) {
        var ex = stateTaxDocumentExpiryState(d);
        return (
          <div key={d.id} style={{ display: 'grid', gridTemplateColumns: '130px 1fr auto', gap: '4px 12px', alignItems: 'center', padding: '4px 0', fontSize: 12 }} data-testid={'state-tax-doc-' + d.id}>
            <div style={{ color: '#111' }}>{stateTaxDocumentKindLabel(d.kind)}</div>
            <div style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#555552' }}>
              <button type="button" onClick={function() { open(d); }} style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', color: '#111', cursor: 'pointer', textDecoration: 'underline' }}>{d.filename || 'file'}</button>
              {d.label ? <span> · {d.label}</span> : null}
              {d.expiresOn ? <span style={{ color: ex === 'expired' ? '#C8322A' : ex === 'soon' ? '#B8860B' : '#888880' }}> · {ex === 'expired' ? 'expired ' : 'expires '}{d.expiresOn}</span> : null}
            </div>
            <button type="button" style={Object.assign({}, linkBtn, { height: 22, padding: '0 8px', fontSize: 10 })} onClick={function() { if (p.onRemove) p.onRemove(d); }}>Remove</button>
          </div>
        );
      })}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
        <select value={kind} onChange={function(e) { setKind(e.target.value); }} style={sel} data-testid={'state-tax-doc-kind-' + rec.id}>
          {STATE_TAX_DOCUMENT_KINDS.map(function(k) { return <option key={k[0]} value={k[0]}>{k[1]}</option>; })}
        </select>
        <input type="date" value={expires} onChange={function(e) { setExpires(e.target.value); }} style={Object.assign({}, sel, { width: 140 })} title="Expires (optional)" data-testid={'state-tax-doc-expires-' + rec.id} />
        <button type="button" style={linkBtn} disabled={busy} onClick={function() { if (inputRef.current) inputRef.current.click(); }}>{busy ? 'Uploading…' : '+ Upload'}</button>
        <input ref={inputRef} type="file" accept="application/pdf,image/*" style={{ display: 'none' }} onChange={onPick} data-testid={'state-tax-doc-input-' + rec.id} />
      </div>
      {err && <div style={{ fontSize: 11, color: '#C8322A', marginTop: 6 }}>{err}</div>}
    </div>
  );
}

function FieldRow(p) {
  if (!p.value) return null;
  var isCopied = p.fieldId && p.copiedField === p.fieldId;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr auto', gap: 8, alignItems: 'center', padding: '4px 0' }}>
      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#888' }}>{p.label}</div>
      <div style={{ fontSize: 12, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.value}</div>
      {p.onCopy && (
        <button style={{ height: 22, padding: '0 8px', borderRadius: 11, border: '0.5px solid #E0E0DC', background: isCopied ? '#1A5C38' : '#fff', color: isCopied ? '#fff' : '#666', fontSize: 10, fontFamily: 'inherit', cursor: 'pointer' }}
          onClick={function() { p.onCopy(p.value, p.fieldId); }}>
          {isCopied ? 'Copied!' : 'Copy'}
        </button>
      )}
    </div>
  );
}

function StateTaxModal(p) {
  var initial = p.rec || {};
  var fs = useState(initial); var f = fs[0]; var setF = fs[1];
  function set(k) { return function(v) { setF(function(prev) { var n = Object.assign({}, prev); n[k] = v; return n; }); }; }

  function onStateChange(code) {
    var match = US_STATES.find(function(row) { return row[0] === code; });
    setF(function(prev) {
      return Object.assign({}, prev, { stateCode: code, stateName: match ? match[1] : prev.stateName });
    });
  }

  function submit() {
    if (!f.stateCode || !f.stateName) {
      alert('State is required.');
      return;
    }
    p.onSave(f);
  }

  var isNew = !f.id;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={function(e) { if (e.target === e.currentTarget) p.onClose(); }}>
      <div style={{ width: 620, maxWidth: '94vw', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: 10, padding: 24, boxShadow: '0 8px 40px rgba(0,0,0,0.3)' }}>

        <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 18 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#111' }}>{isNew ? 'Add State or City' : 'Edit ' + (f.jurisdiction ? f.jurisdiction + ', ' : '') + (f.stateName || f.stateCode)}</div>
          <div style={{ flex: 1 }} />
          <button onClick={p.onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: '#999', cursor: 'pointer', lineHeight: 1, padding: 0 }}>×</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={lbl}>State</label>
            <select value={f.stateCode || ''} onChange={function(e) { onStateChange(e.target.value); }} style={inp}>
              <option value="">— Select —</option>
              {US_STATES.map(function(row) {
                return <option key={row[0]} value={row[0]}>{row[0]} · {row[1]}</option>;
              })}
            </select>
          </div>
          <div>
            <label style={lbl}>City / district · blank = the state itself</label>
            <input value={f.jurisdiction || ''} onChange={function(e) { set('jurisdiction')(e.target.value); }} style={inp} placeholder="Vail" />
          </div>
          <div>
            <label style={lbl}>Status</label>
            <select value={f.status || 'active'} onChange={function(e) { set('status')(e.target.value); }} style={inp}>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="not_required">Not required</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={lbl}>Resale / Registration ID</label>
            <input value={f.registrationId || ''} onChange={function(e) { set('registrationId')(e.target.value); }} style={inp} />
          </div>
          <div>
            <label style={lbl}>Business Partner / Other ID</label>
            <input value={f.businessPartnerId || ''} onChange={function(e) { set('businessPartnerId')(e.target.value); }} style={inp} placeholder="Optional" />
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Account Email</label>
          <input value={f.accountEmail || ''} onChange={function(e) { set('accountEmail')(e.target.value); }} style={inp} placeholder="finance@cimonstarr.com" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={lbl}>Username</label>
            <input value={f.username || ''} onChange={function(e) { set('username')(e.target.value); }} style={inp} autoComplete="off" />
          </div>
          <div>
            <label style={lbl}>Password</label>
            <input type="text" value={f.password || ''} onChange={function(e) { set('password')(e.target.value); }} style={inp} autoComplete="off" />
          </div>
          <div>
            <label style={lbl}>PIN</label>
            <input type="text" value={f.pin || ''} onChange={function(e) { set('pin')(e.target.value); }} style={inp} autoComplete="off" placeholder="WYIFS emails one" data-testid="state-tax-form-pin" />
          </div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={lbl}>Portal URL</label>
          <input value={f.portalUrl || ''} onChange={function(e) { set('portalUrl')(e.target.value); }} style={inp} placeholder="https://..." />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={lbl}>Resale Cert Form</label>
            <input value={f.certFormLabel || ''} onChange={function(e) { set('certFormLabel')(e.target.value); }} style={inp} placeholder="e.g. CA CDTFA-230" />
          </div>
          <div>
            <label style={lbl}>Support Phone</label>
            <input value={f.supportPhone || ''} onChange={function(e) { set('supportPhone')(e.target.value); }} style={inp} placeholder="(800) 555-1212" />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={lbl}>Notes</label>
          <textarea value={f.notes || ''} onChange={function(e) { set('notes')(e.target.value); }} rows={3}
            style={Object.assign({}, inp, { resize: 'vertical', minHeight: 70 })} />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={p.onClose} style={btnGhost}>Cancel</button>
          <button onClick={submit} style={btnPrimary}>Save</button>
        </div>
      </div>
    </div>
  );
}
